import React, { useState, useEffect, useMemo } from "react"
import { OrderEntity } from "../../../Objects/Order/OrderEntity"
import { EOrderStatusType } from "../../../Enum/EOrderStatus"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { EOrderCurrentStatus } from "../../../Enum/EOrderCurrentStatus"
import { EOrderItemChargeType } from "../../../Enum/EOrderItemChargeType"
import { useSaveOrder } from "../../../hooks/Order/useSaveOrder"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"
import { EOrderPlaceType } from "../../../Enum/EOrderPlaceType"
import { MealItem } from "./MealItem"
import { OrderDrink } from "../../../Objects/Order/OrderDrink"
import { ButtonIcon } from "../../../components/Button/Button"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { faPrint } from "@fortawesome/free-solid-svg-icons"
import { EDeliveryStatusType } from "../../../Enum/EDeliveryStatusType"
import { RenderDeliveryDetails } from "./RenderDeliveryDetails"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { OrderPrintOptionsPupop } from "../../../components/OrderPrintOptionsPupop/OrderPrintOptionsPupop"

interface OrderCardProps {
  order: OrderEntity
  type: EOrderStatusType | "ALL"
  station: "KITCHEN" | "BAR"
  isSeparatedMode: boolean
  onUpdate: () => void
  onUpdateStatus: (id: number, status: EOrderStatusType) => void
  onSelectCourier: (order: OrderEntity) => void
  onCancelSearch: (deliveryId: number) => void
}

export function useMinutesTimer(startDate?: string | Date) {
  const [minutes, setMinutes] = useState(0)

  useEffect(() => {
    if (!startDate) return

    const update = () => {
      const start = new Date(startDate).getTime()
      const now = Date.now()
      setMinutes(Math.floor((now - start) / 60000))
    }

    update()
    const interval = setInterval(update, 60000)

    return () => clearInterval(interval)
  }, [startDate])

  return minutes
}

export const KitchenOrderCard: React.FC<OrderCardProps> = ({
  order,
  type,
  station,
  isSeparatedMode,
  onUpdate,
  onUpdateStatus,
  onSelectCourier,
  onCancelSearch,
}) => {
  const { t } = useTranslation("frontend")

  const { setNewConfirmation } = useConfirmation()

  const { handleSaveItemOrderStatus } = useSaveOrder()

  const [isPrintTargetModalOpen, setIsPrintTargetModalOpen] = useState(false)

  const isStorePickupOrder = order.eOrderPlaceType === EOrderPlaceType.Delivery && order.isStorePickup

  const getStatusStyle = () => {
    if (order.isCancel) {
      return "bg-red-100"
    }

    switch (order.eOrderStatusType) {
      case EOrderStatusType.Pending:
        return "bg-blue-50"
      case EOrderStatusType.Preparing:
        return "bg-amber-300/50"
      case EOrderStatusType.PreparingFinished:
        return "bg-purple-300/50"
      case EOrderStatusType.InDelivery:
        return "bg-orange-300/50"
      case EOrderStatusType.Delivered:
        return "bg-green-100"
      default:
        return "bg-blue-50"
    }
  }

  const stationMeals = useMemo(() => {
    const meals = !isSeparatedMode || station === "KITCHEN" ? order.orderMeals : []

    return meals.filter((meal) => meal.chargeType !== EOrderItemChargeType.RodizioAccess)
  }, [order.orderMeals, isSeparatedMode, station])

  const stationDrinks = useMemo(() => {
    const drinks = !isSeparatedMode ? order.orderDrinks : station === "BAR" ? order.orderDrinks : []

    return drinks.filter((drink) => drink.chargeType !== EOrderItemChargeType.RodizioAccess)
  }, [order.orderDrinks, isSeparatedMode, station])

  const stationItems = useMemo(() => [...stationMeals, ...stationDrinks], [stationMeals, stationDrinks])

  const allOrderItems = useMemo(() => order.getAllGenericFoods(), [order])

  const isAllItemsComplete = useMemo(() => {
    if (stationItems.length === 0) {
      return true
    }

    const hasFinished = stationItems.every(
      (e) => e.currentStatus === EOrderCurrentStatus.Finished || e.currentStatus === EOrderCurrentStatus.Closed,
    )

    const hasInvalid = stationItems.some(
      (e) => e.currentStatus !== EOrderCurrentStatus.Finished && e.currentStatus !== EOrderCurrentStatus.Closed,
    )

    return hasFinished && !hasInvalid
  }, [stationItems])

  const isAllOrderItemsComplete = useMemo(() => {
    if (!allOrderItems || allOrderItems.length === 0) {
      return true
    }

    return allOrderItems.every(
      (item) =>
        item.currentStatus === EOrderCurrentStatus.Finished || item.currentStatus === EOrderCurrentStatus.Closed,
    )
  }, [allOrderItems])

  const handleStatusItem = (genericFoodId: number, isNextStep: boolean) => {
    handleSaveItemOrderStatus(order.id, genericFoodId, isNextStep, () => onUpdate())
  }

  const matchesFilter = (e: OrderDrink) =>
    e.currentStatus === type || type === "ALL" || e.currentStatus === EOrderCurrentStatus.Closed

  const renderSelectCourierButton = (order: OrderEntity) => (
    <CanView permission={Permissions.CanEditeKitchen}>
      <button
        disabled={!isAllOrderItemsComplete}
        onClick={() => onSelectCourier(order)}
        className="w-full ring-2 ring-orange-500 bg-orange-100 hover:bg-orange-400
         text-gray-800 font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed
         transition-all duration-200 hover:scale-[1.02]"
        data-interaction-description-key="interactionDescriptions:pages.kitchenPage.components.kitchenOrderCard.button.onSelectCourierOrder"
      >
        SELECIONAR ENTREGADOR
      </button>
    </CanView>
  )

  const renderDeliverySection = (order: OrderEntity) => {
    if (order.isStorePickup) {
      return (
        <div className="w-full rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-center text-xs font-bold text-emerald-800">
          {t("kitchen.pickup_order_no_courier")}
        </div>
      )
    }

    const delivery = order.deliveryEntity

    if (
      !delivery ||
      delivery.status === EDeliveryStatusType.Canceled ||
      delivery.status === EDeliveryStatusType.Expired
    ) {
      return renderSelectCourierButton(order)
    }

    return <RenderDeliveryDetails delivery={delivery} onCancelSearch={onCancelSearch} />
  }

  const canShowActionButtons = useMemo(() => {
    const delivery = order.deliveryEntity

    if (!delivery || order.eOrderPlaceType !== EOrderPlaceType.Delivery) {
      return true
    }

    return (
      !delivery || delivery.status === EDeliveryStatusType.Canceled || delivery.status === EDeliveryStatusType.Expired
    )
  }, [order.deliveryEntity])

  return (
    <Animate type={AnimationTypes.slideRightIn}>
      <>
        <div
          className={`
          flex flex-col rounded-2xl ring-2 ring-slate-200 shadow-md
          transition-all duration-300
          hover:-translate-y-1 hover:shadow-xl
          ${getStatusStyle()}
        `}
        >
          <div className="p-4 border-b bg-gray-50/50 backdrop-blur-sm rounded-t-2xl">
            <div className="flex justify-between items-start">
              <div className="w-full flex flex-col justify-between">
                <div className="flex flex-row w-full justify-between items-center">
                  {order.tableId > 0 ? (
                    <span className="text-xs font-bold uppercase text-slate-500">Mesa {order.tableId}</span>
                  ) : (
                    <span className="text-xs font-bold uppercase text-slate-500">
                      {isStorePickupOrder ? t("kitchen.pickup_order_title") : "Pedido Online"}
                    </span>
                  )}

                  <div className="flex flex-row justify-end items-center">
                    <span className="text-xs text-slate-500 font-bold mr-2">{order.date.toReadableDate()}</span>

                    <ButtonIcon
                      typeButton="button"
                      sizeNumber={SIZE.buttonExpandIcon}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      icon={faPrint}
                      onClick={() => setIsPrintTargetModalOpen(true)}
                      size="small"
                      descriptionKey="interactionDescriptions:pages.kitchenPage.components.kitchenOrderCard.button.setIsPrintTargetModalOpenTrue"
                    />
                  </div>
                </div>

                <div className="flex flex-row w-full justify-between items-center">
                  <h3 className="text-lg font-black text-red-500">
                    {isStorePickupOrder
                      ? t("kitchen.pickup_order_label")
                      : order.eOrderPlaceType.toOrderPlaceTypeString()}{" "}
                    #{order.id}
                  </h3>

                  <h3 className="text-sm font-black text-orange-700">{order.status.toEOrderTypeString()}</h3>
                </div>
              </div>
            </div>

            <div className="mt-2 text-sm font-semibold text-slate-600">{order.getCustomerDisplayName()}</div>

            {order.isTableSelfServiceOrder() && (
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="inline-flex rounded-full border-2 border-violet-300 bg-white px-3 py-1 text-[11px] font-black uppercase tracking-wider text-violet-700">
                  {t("order:label_table_self_service_order")}
                </span>

                <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-bold text-slate-700">
                  {t("order:label_cpf")}: {order.getCustomerDisplayCpf()}
                </span>
              </div>
            )}

            {isStorePickupOrder && (
              <div className="mt-2 inline-flex rounded-full border-2 border-emerald-400 bg-emerald-100 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-emerald-700">
                {t("kitchen.pickup_badge")}
              </div>
            )}
          </div>

          <div className="flex-1 p-2 space-y-6 bg-white rounded-b-2xl">
            {stationMeals.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase mb-3 tracking-wider">{t("kitchen.meals")}</h4>

                {stationMeals
                  .filter(
                    (e) => e.currentStatus === type || type === "ALL" || e.currentStatus === EOrderCurrentStatus.Closed,
                  )
                  .map((meal, index) => (
                    <MealItem
                      key={meal.id ?? index}
                      meal={meal}
                      orderId={order.id}
                      canShowActionButtons={canShowActionButtons}
                      isDelivery={order.eOrderPlaceType === EOrderPlaceType.Delivery}
                      handleStatusItem={handleStatusItem}
                    />
                  ))}
              </div>
            )}

            {stationDrinks.length > 0 && (
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <div className="flex items-center gap-2 mb-4">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Bebidas</h4>
                </div>

                {stationDrinks.filter(matchesFilter).map((meal, index) => (
                  <MealItem
                    key={meal.id ?? index}
                    meal={meal}
                    canShowActionButtons={canShowActionButtons}
                    orderId={order.id}
                    isDelivery={order.eOrderPlaceType === EOrderPlaceType.Delivery}
                    handleStatusItem={handleStatusItem}
                  />
                ))}

                <div className="space-y-4">
                  {stationDrinks
                    .filter((e) => !matchesFilter(e))
                    .map((drink, idx) => (
                      <div
                        key={drink.id ?? idx}
                        className="flex flex-col border-b border-slate-200 pb-3 last:border-none last:pb-0"
                      >
                        <div className="font-semibold text-slate-800">{drink.drinkEntity.genericFoodEntity.name}</div>

                        {drink.description && <p className="text-xs text-slate-500 italic mt-1">{drink.description}</p>}
                      </div>
                    ))}
                </div>
              </div>
            )}

            <CanView permission={Permissions.CanEditeKitchen}>
              {!order.isFinished() && (
                <>
                  {isAllItemsComplete && isAllOrderItemsComplete && order.eOrderPlaceType === EOrderPlaceType.Local && (
                    <div className="p-4 bg-slate-50 border-t">
                      <button
                        onClick={() =>
                          setNewConfirmation(
                            "Marcar como entregue",
                            "Tem certeza que deseja marcar este pedido como entregue?",
                            () => onUpdateStatus(order.id, EOrderStatusType.Delivered),
                          )
                        }
                        className="w-full ring-2 ring-emerald-600 bg-emerald-200 hover:bg-emerald-500
                           text-gray-800 font-bold py-3 rounded-lg
                           transition-all duration-200 hover:scale-[1.02]"
                        data-interaction-description-key="interactionDescriptions:pages.kitchenPage.components.kitchenOrderCard.button.setNewConfirmationMarcarComoEntregueTemCertezaQueDesejaMarca"
                      >
                        MARCAR COMO ENTREGUE
                      </button>
                    </div>
                  )}

                  {isAllItemsComplete &&
                    isAllOrderItemsComplete &&
                    order.eOrderPlaceType === EOrderPlaceType.Delivery && (
                      <div className="p-1 space-y-2">
                        {canShowActionButtons && (
                          <button
                            onClick={() =>
                              setNewConfirmation(
                                "Marcar como entregue",
                                "Tem certeza que deseja marcar este pedido como entregue?",
                                () => onUpdateStatus(order.id, EOrderStatusType.Delivered),
                              )
                            }
                            className="w-full ring-2 ring-emerald-600 bg-emerald-200 hover:bg-emerald-500 text-gray-800 font-bold py-3 rounded-lg transition-all duration-200 hover:scale-[1.02]"
                            data-interaction-description-key="interactionDescriptions:pages.kitchenPage.components.kitchenOrderCard.button.setNewConfirmationMarcarComoEntregueTemCertezaQueDesejaMarca2"
                          >
                            MARCAR COMO ENTREGUE
                          </button>
                        )}

                        {renderDeliverySection(order)}
                      </div>
                    )}
                </>
              )}
            </CanView>
          </div>
        </div>

        <OrderPrintOptionsPupop
          isOpen={isPrintTargetModalOpen}
          title={t("kitchen.title_print_target")}
          orderId={order.id}
          printScope={station === "BAR" ? "bar" : "kitchen"}
          onClose={() => setIsPrintTargetModalOpen(false)}
        />
      </>
    </Animate>
  )
}
